import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import PerformanceBadge from '../components/PerformanceBadge';
import FitnessProgressView from '../components/FitnessProgressView';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from 'recharts';
import SafeChart from '../components/SafeChart';
import { normalizeProgress, normalizeLatestResults } from '../lib/assessmentNormalizer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category colors for charts. Phase-1 canonical categories + legacy
// fallbacks so historical records still render correctly.
const CATEGORY_COLORS = {
  'Lower Body Strength': '#8b5cf6',
  'Upper Body Strength': '#7c3aed',
  Speed: '#3b82f6',
  Agility: '#10b981',
  Power: '#f59e0b',
  Endurance: '#ef4444',
  Flexibility: '#ec4899',
  Strength: '#8b5cf6',   // legacy
  Resistance: '#ef4444', // legacy
};

// Phase-1-aware category groups (cover canonical + legacy names).
const STRENGTH_CATEGORIES = new Set([
  'Lower Body Strength',
  'Upper Body Strength',
  'Strength',
]);
const TIME_CATEGORIES = new Set(['Speed', 'Agility']);
const DISTANCE_CATEGORIES = new Set(['Endurance', 'Resistance']);

const PlayerAssessments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [categories, setCategories] = useState({});
  const [progress, setProgress] = useState(null);
  const [latestResults, setLatestResults] = useState(null);
  const [athleteId, setAthleteId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!['Player', 'Parent'].includes(user?.role)) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, userRes] = await Promise.all([
        axios.get(`${API}/assessments/categories`),
        axios.get(`${API}/auth/me`)
      ]);
      
      setCategories(categoriesRes.data);
      
      const athleteId = userRes.data.athlete_id;
      setAthleteId(athleteId);
      if (athleteId) {
        const [progressRes, latestRes] = await Promise.all([
          axios.get(`${API}/assessments/athlete/${athleteId}/history`),
          axios.get(`${API}/assessments/athlete/${athleteId}/latest`)
        ]);
        const cats = categoriesRes.data;
        setProgress({
          ...progressRes.data,
          progress: normalizeProgress(progressRes.data.progress, cats),
        });
        setLatestResults(normalizeLatestResults(latestRes.data, cats));
      }
    } catch (error) {
      toast.error('Failed to fetch assessment data');
    } finally {
      setLoading(false);
    }
  };

  const getResultValue = (result) => {
    if (!result) return '-';
    // Phase 1: reps no longer displayed on strength results.
    if (STRENGTH_CATEGORIES.has(result.category)) {
      return result.weight ? `${result.weight}kg` : '-';
    }
    if (TIME_CATEGORIES.has(result.category)) return result.time ? `${result.time}s` : '-';
    if (DISTANCE_CATEGORIES.has(result.category)) return result.distance ? `${result.distance}m` : '-';
    return result.measurement ? `${result.measurement}cm` : '-';
  };

  const getChangeIndicator = (current, previous, lowerIsBetter = false) => {
    if (!current || !previous) return null;

    let currentVal, prevVal;
    if (STRENGTH_CATEGORIES.has(current.category)) {
      currentVal = current.weight || current.score;
      prevVal = previous.weight || previous.score;
    } else if (TIME_CATEGORIES.has(current.category)) {
      currentVal = current.time;
      prevVal = previous.time;
      lowerIsBetter = true;
    } else if (DISTANCE_CATEGORIES.has(current.category)) {
      currentVal = current.distance;
      prevVal = previous.distance;
    } else {
      currentVal = current.measurement;
      prevVal = previous.measurement;
    }
    
    if (!currentVal || !prevVal) return null;
    
    const diff = currentVal - prevVal;
    const isImproved = lowerIsBetter ? diff < 0 : diff > 0;
    const percentage = ((Math.abs(diff) / prevVal) * 100).toFixed(1);
    
    return (
      <span className={`text-xs font-medium ${isImproved ? 'text-emerald-600' : 'text-red-500'}`}>
        {isImproved ? '↑' : '↓'} {percentage}%
      </span>
    );
  };

  const renderProgressChart = (testName, data) => {
    if (!data || data.history.length < 2) return null;
    
    const chartData = data.history.map(h => ({
      date: h.date,
      value: h.value
    }));
    
    return (
      <div className="h-40 mt-2">
        <SafeChart height={160}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="date" fontSize={10} tick={{ fill: '#6b7280' }} />
            <YAxis fontSize={10} tick={{ fill: '#6b7280' }} />
            <Tooltip 
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={CATEGORY_COLORS[data.category]} 
              strokeWidth={2}
              dot={{ fill: CATEGORY_COLORS[data.category], strokeWidth: 2 }}
            />
          </LineChart>
        </SafeChart>
      </div>
    );
  };

  if (!['Player', 'Parent'].includes(user?.role)) return null;

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {user?.role === 'Parent' ? "Your Child's" : 'My'} Assessment Results
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">Physical performance evaluations and progress over time</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : !progress || !latestResults?.session ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Assessments Yet</h3>
              <p className="text-gray-500 dark:text-gray-500">
                Physical assessments will appear here once your coach records them.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Latest Assessment Summary */}
            <Card className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Latest Assessment</CardTitle>
                  <Badge variant="outline" className="bg-white dark:bg-gray-800">
                    {latestResults.session.session_date}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {Object.keys(categories)
                    .filter((k) => !k.startsWith('_'))
                    .map(category => {
                    const categoryResults = latestResults.results[category] || [];
                    const count = categoryResults.length;
                    return (
                      <div key={category} className="text-center">
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2"
                          style={{ backgroundColor: `${CATEGORY_COLORS[category]}20` }}
                        >
                          <span className="text-lg font-bold" style={{ color: CATEGORY_COLORS[category] }}>
                            {count}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{category}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">{count === 1 ? 'test' : 'tests'}</p>
                      </div>
                    );
                  })}
                </div>
                
                {latestResults.session.evaluator_notes && (
                  <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border">
                    <p className="text-sm text-gray-500 dark:text-gray-500 mb-1">Coach Notes:</p>
                    <p className="text-gray-700 dark:text-gray-300">{latestResults.session.evaluator_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Detailed Results by Category — Phase 3 unified progress view */}
            <Card>
              <CardHeader>
                <CardTitle>Detailed Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <FitnessProgressView athleteId={athleteId} mode="simple" />
              </CardContent>
            </Card>

            {/* Assessment History */}
            {progress.total_sessions > 1 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Assessment History</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 dark:text-gray-500">
                    Total assessments completed: <strong>{progress.total_sessions}</strong>
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default PlayerAssessments;
